import type { ITemplateAssignedType, TemplateInfo } from '~/types/template';
import type { Campaign, Variant } from '~/modules/campaign/types';

import { useLocationHandlers } from './useLocationHandlers';
import { TEMPLATE_ASSIGNED } from '~/constants/template';
import { useCampaignPreview } from '~/modules/campaign';
import { useShopifyThemeInfo } from '~/modules/shopify';
import { TemplateType } from '~/types/template';
import { isTemplateShopify } from '~/utils';
import { useMemo } from 'react';

export const useViewEditorShopify = () => {
  const { shopifyThemeId } = useShopifyThemeInfo();
  const { getShopifyAdminUrl, handleOpenUrlInNewTab } = useLocationHandlers();
  const { parseUrlPreviewBy } = useCampaignPreview();

  const shopifyThemePath = useMemo(() => {
    return getShopifyAdminUrl(`themes/${shopifyThemeId}/editor`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopifyThemeId]);

  const parseUrlEditorShopify = (
    templateInfo: TemplateInfo,
    previewPath: string | undefined,
  ): string => {
    const newUrl = new URL(shopifyThemePath);
    switch (templateInfo.type) {
      case TemplateType.HOME:
        if (isTemplateShopify(templateInfo.providerSource)) {
          const view = templateInfo.suffix ?? TemplateType.HOME;
          newUrl.searchParams.set('previewPath', `/?view=${view}`);
        }
        return newUrl.href;
      default:
        previewPath && newUrl.searchParams.set('previewPath', previewPath);
        newUrl.searchParams.set('view', templateInfo.suffix);
        return newUrl.href.replace('&view=', '%3Fview%3D');
    }
  };

  const handleEditTemplateShopify = async (
    templateInfo: TemplateInfo,
    variant: Variant,
    campaign?: Campaign,
  ) => {
    let previewPath;
    const templateType = templateInfo.type as unknown as ITemplateAssignedType;
    const isNeedPreviewPath = TEMPLATE_ASSIGNED.includes(templateType);
    if (isNeedPreviewPath) {
      const data = await parseUrlPreviewBy(variant, campaign);
      previewPath = data?.pathname;
    }

    const url = parseUrlEditorShopify(templateInfo, previewPath);
    if (!url) return;

    handleOpenUrlInNewTab(url);
  };

  return { handleEditTemplateShopify };
};
